type ProductImageSettingsBase = {enabled?: boolean | null};

type ProductWithImageSettings<S extends ProductImageSettingsBase> = {
	productImageSettings?: S | null;
	settings?: {productImageSettings?: S | null} | null;
};

export type ResolvedProductImageSettings<S extends ProductImageSettingsBase> = {
	imageSettings: S;
	/** Whether the image settings come from the shared standard settings instead of the product. */
	isStandard: boolean;
};

/**
 * Returns the most specific enabled product image settings for a product with standard settings.
 * Settings on the product itself override the ones from its standard settings.
 */
export function resolveProductImageSettings<S extends ProductImageSettingsBase>(
	product: ProductWithImageSettings<S>,
): ResolvedProductImageSettings<S> | null {
	if (product.productImageSettings?.enabled) {
		return {imageSettings: product.productImageSettings, isStandard: false};
	}

	if (product.settings?.productImageSettings?.enabled) {
		return {imageSettings: product.settings.productImageSettings, isStandard: true};
	}

	return null;
}

/**
 * Whether the product preview image is the generic image from the product's standard settings.
 */
export function usesStandardProductImage<S extends ProductImageSettingsBase>(
	product: ProductWithImageSettings<S>,
) {
	return resolveProductImageSettings(product)?.isStandard ?? false;
}
